import { useState, useCallback } from "react";
import {
  Button,
  Card,
  CardPreview,
  makeStyles,
} from "@fluentui/react-components";
import { Dialpad } from "@azure/communication-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { Call16Filled } from "@fluentui/react-icons";
import PhoneSearchInput from "./PhoneSearchInput";

// Matches strings that are dialable as a PSTN number: digits, +, *, #, spaces,
// parentheses, dashes and dots. Rejects plain text like a person's name.
const DIALABLE_RE = /^[0-9+*#\s().,-]+$/;
const isDialableNumber = (val) => DIALABLE_RE.test((val || "").trim());

const useStyles = makeStyles({
  card: {
    maxWidth: "480px",
    width: "100%",
    margin: "0 auto",
  },
  header: {
    paddingBottom: "2px",
    //alignItems: "flex-start", //for agent name to appearon top, irrepective of oof msg
  },
  dialpadWrapper: {
    padding: "2.5%",
    width: "95%",
  },
  status: {
    marginTop: "10px",
    padding: "6px 10px",
    backgroundColor: "#F3F2F1",
    borderRadius: "6px",
  },
  error: {
    color: "#b10e1e",
    fontWeight: 600,
    marginTop: "10px",
  },
  footerRow: {
    display: "flex !important",
    justifyContent: "center",
    marginBottom: "45px",
    alignItems: "center",
  },
});

export default function PhonePanel({
  call,
  incomingCall,
  callState,
  onPlaceCall,
}) {
  const styles = useStyles();
  const isMobile = useIsMobile();

  const [dialNumber, setDialNumber] = useState("");
  // Tracks a selected Microsoft user; their Teams ID is used when Call is pressed
  const [selectedMSUser, setSelectedMSUser] = useState(null);
  // Tracks a selected Salesforce record; metadata is passed when Call is pressed
  const [selectedSFRecord, setSelectedSFRecord] = useState(null);
  // Mirrors the source dropdown selection inside PhoneSearchInput
  const [searchSource, setSearchSource] = useState("microsoft");

  const isConnected = callState === "Connected";
  const hasIncomingCall = incomingCall && !call;
  const handleStartCall = () => {
    if (selectedMSUser) {
      // placeCallInternal checks for '@' in the number to detect internal (Teams) calls.
      // When internal, it reads params.graphUserId for the ACS microsoftTeamsUserId target
      // and sets callToPhone=null (avoids Salesforce 40-char field overflow).
      // IMPORTANT: params must be a comma-separated string — placeCallInternal parses it
      // via .split(",") into an object, so passing a plain object would throw TypeError.
      const upn =
        selectedMSUser._raw?.mail ||
        selectedMSUser._raw?.userPrincipalName ||
        "";
      if (!upn) {
        console.error("[PhonePanel] Cannot place Teams call: no UPN or email available for selected user.");
        return;
      }
      onPlaceCall(upn, {
        recordId: selectedMSUser.recordId,
        recordName: selectedMSUser.name,
        objectType: "MSUser",
        params: `graphUserId=${encodeURIComponent(selectedMSUser.recordId)}`,
      });
    } else if (dialNumber && isDialableNumber(dialNumber)) {
      // Use SF record metadata if the number in the input matches the selected record
      if (
        selectedSFRecord &&
        dialNumber === selectedSFRecord.phone &&
        selectedSFRecord.recordType !== "User"
      ) {
        onPlaceCall(dialNumber, {
          recordId: selectedSFRecord.recordId,
          recordName: selectedSFRecord.name,
          objectType: selectedSFRecord.recordType,
        });
      } else {
        onPlaceCall(dialNumber);
      }
    }
  };

  const handleSelectResult = useCallback(
    (row) => {
      if (!row) {
        // Selection cleared (e.g. user pressed X in MS mode)
        setSelectedMSUser(null);
        setSelectedSFRecord(null);
        return;
      }
      if (row.source === "microsoft") {
        // Show name in input; Call button will use the Teams ID
        setSelectedMSUser(row);
        setSelectedSFRecord(null);
      } else {
        // Salesforce: store selection so Call button can use the metadata
        setSelectedMSUser(null);
        setSelectedSFRecord(row);
      }
    },
    [],
  );

  return (
    <Card className={styles.card} appearance="filled">
      <CardPreview>
        {!isConnected && (
          <>
            {/* Dialpad */}
            <div className={styles.dialpadWrapper}>
              {/* Input + typeahead dropdown */}
              <PhoneSearchInput
                value={dialNumber}
                onChange={(val) => {
                  setDialNumber(val);
                  // If user edits the input manually, clear any stored selections
                  if (!val) {
                    setSelectedMSUser(null);
                    setSelectedSFRecord(null);
                  }
                }}
                onSelect={handleSelectResult}
                onSourceChange={(src) => {
                  setSearchSource(src);
                  // Changing source clears any active selection
                  setSelectedMSUser(null);
                }}
              />

              <Dialpad
                dialpadMode="dtmf"
                longPressTrigger={isMobile ? "touch" : "mouseAndTouch"}
                onClickDialpadButton={(buttonValue) => {
                  setDialNumber((prev) => (prev || "") + buttonValue);
                }}
                styles={{
                  root: {
                    background: "#ffffff",
                  },
                  button: {
                    root: {
                      height: isMobile ? "5rem" : "4rem",
                      width: isMobile ? "6rem" : "5rem",
                      lineHeight: "inherit",
                      background: "#ffffff",
                    },
                    rootHovered: {
                      background: "#e5e7eb",
                      span: {
                        color: "#0f6cbd !important",
                      },
                    },
                    rootPressed: {
                      background: "#d1d5db",
                      color: "#0f6cbd",
                    },
                  },
                  digit: {
                    color: "#0f6cbd",
                    fontWeight: 600,
                  },
                  letter: {
                    color: "#0f6cbd",
                    fontWeight: 600,
                  },
                }}
              />
            </div>

            {/* Call Button */}
            <div className={styles.footerRow}>
              <Button
                style={{ width: "80%" }}
                appearance="primary"
                shape="circular"
                disabled={
                  hasIncomingCall ||
                  (searchSource === "microsoft"
                    ? !selectedMSUser && !isDialableNumber(dialNumber)  // MS: picked user OR dialable number
                    : !isDialableNumber(dialNumber))                    // SF / PSTN: must be a dialable number
                }
                icon={<Call16Filled />}
                onClick={handleStartCall}
              >
                Call
              </Button>
            </div>
          </>
        )}
      </CardPreview>
    </Card>
  );
}
